import React, {useEffect, useState} from "react";
import moment from "moment-timezone";
import {useMutation} from "@apollo/client";
import AddBoxIcon from '@mui/icons-material/AddBox';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import {
    Backdrop,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    TextField,
    Typography
} from "@mui/material";
import Grid from '@mui/material/Grid';

import CustomAlert from "../CustomAlert";
import ServiceTypeSelect from "../ServiceTypeSelect";
import CapLevelSelect from "../CapLevelSelect";
import DateTimeSelect from "../DateTimeSelect";
import {
    ALL_PUBLISHING_PLANS,
    CREATE_CONTENT_PUBLISHING_PLAN,
    DELETE_PUBLISHING_PLAN,
    UPDATE_CONTENT_PUBLISHING_PLAN
} from "../../queries/GraphQLQueries";

const ACTION_CREATE = 'Create';
const ACTION_EDIT = 'Edit';
const ACTION_DELETE = 'Delete';

export default function PublishingPlanCrudActionsDialog({currentServiceType, currentCapLevel, contentInfoId, contentInfoName, currentStartTime, currentEndTime, planId, originalPerpetualFlag, crudAction, openPublishingPlanCrudActionsDialog, handleClose}) {

    const [isPerpetuallyPublished, setIsPerpetuallyPublished] = useState(!!originalPerpetualFlag);
    const [publishingStart, setPublishingStart] = useState(currentStartTime ? moment(currentStartTime) : moment().startOf('month'));
    const [publishingEnd, setPublishingEnd] = useState(currentEndTime ? moment(currentEndTime) : moment().endOf('month'));
    const [isDataLoading, setIsDataLoading] = useState(false);
    const [alertMessage, setAlertMessage] = useState(null);

    const [createPlanMutation] = useMutation(CREATE_CONTENT_PUBLISHING_PLAN, {refetchQueries: [{ query: ALL_PUBLISHING_PLANS }, 'GetPublishingPlans'],
        awaitRefetchQueries: true,
        onCompleted: () => {
            setIsDataLoading(false);
        }});
    const [updatePlanMutation] = useMutation(UPDATE_CONTENT_PUBLISHING_PLAN, {refetchQueries: [{ query: ALL_PUBLISHING_PLANS }, 'GetPublishingPlans'],
        awaitRefetchQueries: true,
        onCompleted: () => {
            setIsDataLoading(false);
        }});
    const [deletePlan] = useMutation(DELETE_PUBLISHING_PLAN, {refetchQueries: [{query: ALL_PUBLISHING_PLANS}, "GetPublishingPlans"],
        awaitRefetchQueries: true,
        onCompleted: () => {
            setIsDataLoading(false);
        }});

    const isDelete = crudAction === ACTION_DELETE;

    const backdropStyle = {
        color: '#fff',
        display: 'flex',
        zIndex: 1500
    };

    useEffect(() => {

        if (isPerpetuallyPublished) {
            setPublishingStart(null);
            setPublishingEnd(null);
            return;
        }

        const startOfMonth = moment().startOf('month');
        const endOfMonth = moment().endOf('month');

        if (originalPerpetualFlag || !currentStartTime) {
            setPublishingStart(startOfMonth);
            setPublishingEnd(endOfMonth);
        } else {
            setPublishingStart(moment(currentStartTime));
            setPublishingEnd(moment(currentEndTime));
        }

    }, [originalPerpetualFlag, isPerpetuallyPublished, currentStartTime, currentEndTime]);

    const handleCloseCustomAlert = () => setAlertMessage(null);

    const createPublishingPlan = () => {
        setIsDataLoading(true);
        setAlertMessage({children: 'Creating Plan...', severity: 'success'});
        createPlanMutation({
            variables: {
                contentId: contentInfoId,
                from: publishingStart,
                to: publishingEnd,
                perpetual: isPerpetuallyPublished
            }
        })
        .then(() => handleClose())
        .catch(error => {
            setAlertMessage({children: error?.message, severity: 'error'});
            console.log(error);
        }).finally(() => setIsDataLoading(false));
    };

    const updatePublishingPlan = () => {
        setIsDataLoading(true);
        setAlertMessage({children: 'Updating Plan...', severity: 'success'});
        updatePlanMutation({
            variables: {
                planId,
                from: publishingStart,
                to: publishingEnd,
                perpetual: isPerpetuallyPublished
            },
        })
        .then(() => handleClose())
        .catch(error => {
            setAlertMessage({children: error?.message, severity: 'error'});
            console.log(error);
        }).finally(() => setIsDataLoading(false));
    };

    const deletePublishingPlan = () => {
        setIsDataLoading(true);
        setAlertMessage({children: 'Deleting plan...', severity: 'success'});
        deletePlan({variables: {planId}})
        .then(() => handleClose())
        .catch(error => {
            setAlertMessage({children: error?.message, severity: 'error'});
            console.log(error);
        }).finally(() => setIsDataLoading(false));
    };

    const handleAction = () => {

        if (crudAction === ACTION_CREATE) {
            createPublishingPlan();
        } else if (crudAction === ACTION_EDIT) {
            updatePublishingPlan();
        } else if (crudAction === ACTION_DELETE) {
            deletePublishingPlan();
        }
    };

    const buildDialogTitleIcon = (crudAction) => {

        const iconMap = {
            [ACTION_CREATE]: <AddBoxIcon sx={{mr: 1}} />,
            [ACTION_EDIT]: <EditIcon sx={{mr: 1}} />,
            [ACTION_DELETE]: <DeleteForeverIcon sx={{mr: 1}} />
        };

        return iconMap[crudAction];
    }

    const buildConfirmationText = (crudAction) => {

        const titleMap = {
            [ACTION_CREATE]: 'Create',
            [ACTION_EDIT]: 'Update',
            [ACTION_DELETE]: 'Remove'
        }

        return titleMap[crudAction];
    }

    const dialogStyle = {display: 'flex', justifyContent: 'start', alignItems: 'center'};

    return <>
        <Backdrop open={isDataLoading} sx={backdropStyle}>
            <CircularProgress color="inherit" />
        </Backdrop>

        {!isDataLoading
            && <Dialog open={openPublishingPlanCrudActionsDialog} onClose={handleClose} disableRestoreFocus>
                <DialogTitle sx={dialogStyle}>{buildDialogTitleIcon(crudAction)} {crudAction} Content Publishing Plan</DialogTitle>
                <DialogContent>
                    <Grid container spacing={0.5}>

                        <Grid size={12}>
                            <Typography variant={"overline"}>Service</Typography>
                        </Grid>
                        <Grid size={6}>
                            <ServiceTypeSelect isNonEditable={true} currentServiceType={currentServiceType} />
                        </Grid>

                        <Grid size={12} sx={{mt: 1}}>
                            <Typography variant={"overline"}>Content</Typography>
                        </Grid>
                        <Grid size={6}>
                            <CapLevelSelect isNonEditable={true} currentCapLevel={currentCapLevel} />
                        </Grid>
                        <Grid size={6}>
                            <TextField label='Content Name' className="text-ellipsis" value={contentInfoName} variant='standard' slotProps={{input: {readOnly: true}}} />
                        </Grid>

                        <Grid size={12} sx={{mt: 1}}>
                            <Typography variant={"overline"}>Content Availability</Typography>
                        </Grid>
                        <Grid size={12}>
                            <FormControlLabel
                            control={<Checkbox checked={isPerpetuallyPublished} disabled={isDelete} onChange={event => setIsPerpetuallyPublished(event.target.checked)} />}
                            sx={{ml: 0.25, mt: -0.5, mb: 1}}
                            label={'Perpetual'}
                            labelPlacement="start" />
                        </Grid>
                        <Grid size={6}>
                            <DateTimeSelect
                            isNonEditable={isDelete || isPerpetuallyPublished}
                            label={'Start Time'}
                            currentDate={publishingStart}
                            handleDateChanged={setPublishingStart}
                            additionalProperties={{minutesStep: 60}}/>
                        </Grid>
                        <Grid size={6}>
                            <DateTimeSelect
                            isNonEditable={isDelete || isPerpetuallyPublished}
                            label={'End Time'}
                            currentDate={publishingEnd}
                            handleDateChanged={setPublishingEnd}
                            additionalProperties={{minTime: publishingStart, disableIgnoringDatePartForTimeValidation: true}} />
                        </Grid>

                    </Grid>
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => handleClose()} variant="contained">Cancel</Button>
                    <Button onClick={handleAction} variant="contained">{buildConfirmationText(crudAction)}</Button>
                </DialogActions>
            </Dialog>
        }

        <CustomAlert alertMessage={alertMessage} handleCloseCustomAlert={handleCloseCustomAlert} />
    </>;
}
